// @ts-nocheck
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useAdminOrgScope } from "@/hooks/useAdminOrgScope";
import { toast } from "sonner";
import {
  Save,
  Loader2,
  AlertCircle,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CheckCircle2,
  Circle,
  AlertTriangle,
  Eye,
  Users,
  Send,
  CalendarCheck,
  IdCard,
  DollarSign,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useEventEditor } from "@/hooks/useEventEditor";
import {
  BasicInfoSection,
  VenuesSection,
  RegistrationSection,
  ConstraintsSection,
  SchedulingRequestsSection,
  LevelsSection,
  TrackingSection,
  ExtrasTicketsSection,
  DetailsSection,
  SponsorsSection,
  CoachAttendanceSection,
  MediaCredentialsSection,
  CoachesMediaSection,
  FaqsSection,
  WaiversSection,
  CircuitSection,
  StandingsSection,
  HotelsSection,
  NotificationsSection,
  BannersSection,
  SchedulingSection,
} from "@/design-system/event-editor";
import { WaitlistSection } from "@/design-system/event-editor/WaitlistSection";
import { EventOpsDialog, type EventOpsPanel } from "@/components/event-ops/EventOpsDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { StepHelpBanner } from "@/design-system/event-editor/StepHelpBanner";

/* ─── Per-step inline help text ─────────────────────────── */
const STEP_HELP: Record<
  string,
  { required?: string[]; body?: string; tip?: string; heading?: string }
> = {
  basic: {
    required: ["Event title", "Sport", "Start date", "Gender designation"],
    tip: "Featured image and description help your event look polished.",
  },
  venues: {
    required: ["Select a tournament headquarters"],
    tip: "Add as many additional venues as you'll need. You can edit later.",
  },
  registration: {
    required: [
      "Set registration open and close dates",
      "Activate at least one division (check Boys or Girls)",
      "Set a price for each activated division",
    ],
    tip: "Activate divisions in the catalog if you don't see one you need.",
  },
  standings: {
    body:
      "Set the priority order for tie-breaker rules. When teams are tied on wins in pool play, these rules determine their rank from top to bottom.",
    tip: "Most events use the default order. Change it only if your sport or format requires a different priority.",
  },
  circuit: {
    body:
      "Optional. Link this event to one or more circuits so games here count toward circuit standings. Manage circuits and scoring configs in the Circuits sidebar page.",
    required: [
      "To skip: leave the master toggle off",
      "To use: toggle on, then check one or more circuits",
    ],
    heading: "How to use",
  },
  scheduling: {
    body:
      "Optional. Charge teams for priority scheduling treatment, and set per-request fees for the Standard tier. Request fees are automatically waived for teams that buy a paid tier.",
    required: [
      "To skip: leave the master toggle off",
      "To use: toggle on, set tier prices and per-request fees",
    ],
    heading: "How to use",
  },
  constraints: {
    body:
      "Optional. Pre-approved scheduling constraints teams can request at registration. Choose which constraints apply, override fees and severity per event. The scheduler honors these automatically.",
    required: [
      "To skip: leave the master toggle off",
      "To use: toggle on, then check templates from the catalog",
    ],
    heading: "How to use",
  },
  scheduling_requests: {
    body:
      "Inbox of scheduling constraints teams submitted at registration. Approve to honor them in the scheduler, or deny if they can't be accommodated. Denying a paid request marks a refund as owed.",
  },
  waitlist: {
    body: "Manage teams waiting for a spot. Offer spots, track responses, set expiration windows.",
  },
  tracking: {
    body:
      "Optional. Track teams confirmed but not registered through the public flow: comp teams, pay-at-door, invitationals, partner teams. The scheduler counts these alongside registered teams.",
  },
  extras: {
    body:
      "Optional. Add merchandise, add-ons, or spectator tickets that registrants can purchase. Leave empty if not selling extras.",
  },
  details: {
    body:
      "Add display content for the public event page: format, rules, schedule info, media gallery, hotel info, live streaming. None required, but more info means a better-looking event page.",
  },
  sponsors: {
    body:
      "Optional. Attach sponsor logos and links from your sponsor catalog. They appear on the public event page and registration confirmations.",
  },
  coach_attendance: {
    body:
      "Optional. Allow college coaches to register their attendance for this event. Configure pricing per division (D1/D2/D3/NAIA/JUCO) and customize the form fields.",
    required: [
      "To skip: leave the master toggle off",
      "To enable: toggle on, set pricing if charging, customize the form",
    ],
    heading: "How to use",
  },
  media_credentials: {
    body:
      "Optional. Allow press and media to apply for credentials to cover this event.",
    required: [
      "To skip: leave the master toggle off",
      "To enable: toggle on, customize the application form, optionally charge a fee",
    ],
    heading: "How to use",
  },
  faqs: {
    body:
      "Pick which catalog FAQs apply to this event. Manage the master FAQ list in the FAQs sidebar page. You can override answers per event without affecting other events.",
  },
  waivers: {
    required: [
      "If team waiver is required: select a team waiver from the catalog",
      "If individual waiver is required: select an individual waiver",
    ],
    tip: "If neither is required, leave both toggles off. Manage waiver templates in the Waivers sidebar page.",
  },
};

/* ─── Step definitions ──────────────────────────────────── */
interface StepDef {
  id: string;
  label: string;
  summary: string;
}

/**
 * Keyed step definitions. Declaration order here is IRRELEVANT —
 * STEP_GROUPS below is the single source of truth for order.
 */
const STEP_DEFS: Record<string, StepDef> = {
  basic: { id: "basic", label: "Basic Info", summary: "Title, dates, sport, gender" },
  venues: { id: "venues", label: "Venues", summary: "Headquarters and additional venues" },
  details: { id: "details", label: "Details", summary: "Format, rules, schedule, media" },
  registration: {
    id: "registration",
    label: "Registration",
    summary: "Required — divisions, pricing, registration window",
  },
  extras: { id: "extras", label: "Extras & Tickets", summary: "Add-ons, spectator tickets" },
  scheduling: {
    id: "scheduling",
    label: "Scheduling Fees",
    summary: "Paid scheduling tiers and per-request fees",
  },
  waivers: { id: "waivers", label: "Waivers", summary: "Waiver text and signature tracking" },
  levels: {
    id: "levels",
    label: "Pools / Levels",
    summary: "Per-event competitive pool labels (A/B/C, Gold/Silver/Bronze, etc.)",
  },
  standings: { id: "standings", label: "Standings", summary: "Tie-breaker rules for pool rankings" },
  constraints: {
    id: "constraints",
    label: "Constraints",
    summary: "Pre-approved scheduling constraints teams can request",
  },
  circuit: { id: "circuit", label: "Circuit", summary: "Link this event to a circuit for standings" },
  sponsors: { id: "sponsors", label: "Sponsors", summary: "Event sponsor logos & links" },
  banners: {
    id: "banners",
    label: "Banners",
    summary: "Advertising banner placements for this event",
  },
  coaches_media: {
    id: "coaches_media",
    label: "Coaches / Media",
    summary: "College coach attendance and media credential applications",
  },
  faqs: { id: "faqs", label: "FAQs", summary: "Frequently asked questions" },
  hotels: { id: "hotels", label: "Hotels", summary: "Stay-to-play and hotel booking" },
  notifications: {
    id: "notifications",
    label: "Notifications",
    summary: "Email notification recipients for event activity",
  },
  // NOTE: "waitlist" and "tracking" are intentionally NOT wizard steps.
  // They are ongoing operational tools opened from the header buttons.
};

/* ─── Step rail groups ──────────────────────────────────── */
interface StepGroupDef {
  label: string;
  stepIds: string[];
}

/** SINGLE SOURCE OF TRUTH for wizard order (rail + Previous/Save & Next). */
const STEP_GROUPS: StepGroupDef[] = [
  { label: "Essentials", stepIds: ["basic", "venues", "details"] },
  {
    label: "Registration & Payments",
    stepIds: ["registration", "extras", "scheduling", "waivers"],
  },
  {
    label: "Competition Format",
    stepIds: ["levels", "standings", "constraints", "circuit"],
  },
  {
    label: "Marketing & Exposure",
    stepIds: ["sponsors", "banners", "coaches_media", "faqs"],
  },
  { label: "Logistics & Comms", stepIds: ["hotels", "notifications"] },
];

/* Dev-only integrity guard: definitions and grouping must match exactly. */
if (import.meta.env.DEV) {
  const grouped = STEP_GROUPS.flatMap((g) => g.stepIds);
  const dupes = grouped.filter((id, i) => grouped.indexOf(id) !== i);
  const missingDef = grouped.filter((id) => !STEP_DEFS[id]);
  const ungrouped = Object.keys(STEP_DEFS).filter((id) => !grouped.includes(id));
  if (dupes.length || missingDef.length || ungrouped.length) {
    throw new Error(
      `[EventEditor] STEP_GROUPS/STEP_DEFS out of sync — duplicated: [${dupes.join(
        ", ",
      )}], grouped but undefined: [${missingDef.join(
        ", ",
      )}], defined but ungrouped: [${ungrouped.join(", ")}]`,
    );
  }
}

/** Derived flat, ordered step list. Never hardcode indexes — resolve by id. */
export const EVENT_STEPS: StepDef[] = STEP_GROUPS.flatMap((g) =>
  g.stepIds.map((id) => STEP_DEFS[id]),
);


const readStoredEditorStep = (eventId?: string) => {
  if (typeof window === "undefined") return "basic";
  const urlStep = new URLSearchParams(window.location.search).get("step");
  if (urlStep) return urlStep;
  return localStorage.getItem(`rle_event_editor_step:${eventId || "new"}`) || "basic";
};

/* ─── Flow Builder (embedded) ───────────────────────────── */
function FlowBuilderTab({ eventId }: { eventId: string | null }) {
  const queryClient = useQueryClient();

  const DEFAULT_NODES = [
    { id: "start", type: "start", title: "Start", x: 50, y: 150 },
    { id: "team-info", type: "form", title: "Team Info", x: 250, y: 100 },
    {
      id: "division-select",
      type: "form",
      title: "Division Select",
      x: 450,
      y: 100,
    },
    {
      id: "roster-size",
      type: "form",
      title: "Roster Size",
      x: 650,
      y: 100,
    },
    { id: "addons", type: "addon", title: "Add-ons", x: 850, y: 100 },
    { id: "waiver", type: "waiver", title: "Waiver", x: 1050, y: 100 },
    { id: "payment", type: "payment", title: "Payment", x: 1250, y: 100 },
    {
      id: "confirmation",
      type: "confirmation",
      title: "Confirmation",
      x: 1450,
      y: 150,
    },
  ];
  const DEFAULT_EDGES = [
    { from: "start", to: "team-info" },
    { from: "team-info", to: "division-select" },
    { from: "division-select", to: "roster-size" },
    { from: "roster-size", to: "addons" },
    { from: "addons", to: "waiver" },
    { from: "waiver", to: "payment" },
    { from: "payment", to: "confirmation" },
  ];

  const { data: flow, isLoading } = useQuery({
    queryKey: ["event-flow", eventId],
    queryFn: async () => {
      if (!eventId) return null;
      const { data, error } = await supabase
        .from("event_registration_flows")
        .select("*")
        .eq("event_id", eventId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!eventId,
  });

  const nodes = flow?.nodes || DEFAULT_NODES;

  const [savingFlow, setSavingFlow] = useState(false);
  const handleSaveFlow = async () => {
    if (!eventId) return;
    setSavingFlow(true);
    const { error } = await supabase
      .from("event_registration_flows")
      .upsert(
        {
          event_id: eventId,
          nodes: nodes,
          edges: flow?.edges || DEFAULT_EDGES,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "event_id" }
      );
    setSavingFlow(false);
    if (error) {
      toast.error("Failed to save flow");
    } else {
      queryClient.invalidateQueries({ queryKey: ["event-flow", eventId] });
      toast.success("Flow saved");
    }
  };

  if (!eventId) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        <p>Save the event first, then configure the registration flow.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const NODE_STYLES: Record<string, string> = {
    start: "bg-muted rounded-full",
    form: "bg-white border-primary border-2",
    addon: "bg-white border-border border",
    waiver: "bg-white border-border border",
    payment: "bg-info-light border-info/20 border",
    confirmation: "bg-success-light rounded-full",
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground">Registration Flow</h3>
        <button
          onClick={handleSaveFlow}
          disabled={savingFlow}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white hover:opacity-90 transition-opacity text-sm font-medium disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {savingFlow ? "Saving..." : "Save Flow"}
        </button>
      </div>

      <div className="border border-border rounded-xl bg-white p-4 overflow-x-auto">
        <div className="flex items-center gap-4 min-w-[900px]">
          {(nodes as any[]).map((node: any, i: number) => (
            <div key={node.id} className="flex items-center gap-2">
              <div
                className={cn(
                  "px-4 py-3 rounded-xl text-center min-w-[100px] shadow-sm",
                  NODE_STYLES[node.type] || "bg-white border border-border"
                )}
              >
                <p className="text-xs font-bold text-foreground">
                  {node.title}
                </p>
              </div>
              {i < (nodes as any[]).length - 1 && (
                <div className="text-muted-foreground text-lg">&rarr;</div>
              )}
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Visual canvas editor coming in a future slice. Nodes and edges persist
          as JSON.
        </p>
      </div>
    </div>
  );
}

/* ─── Step completion check ─────────────────────────────── */
type StepStatus = "complete" | "active" | "incomplete" | "error";

/**
 * Step status rules:
 * - REQUIRED steps (basic, venues, registration, waivers): must be configured.
 *   Returns "complete" when valid, "incomplete" when missing required data.
 * - OPTIONAL steps (constraints, waitlist, tracking, coupons, sponsors, faqs,
 *   coach_attendance, media_credentials, stay_to_play): when the toggle is OFF
 *   they are "complete" (intentionally skipped). When toggled ON but missing
 *   required sub-config they are "error" (⚠).
 */
function useStepStatuses(
  steps: StepDef[],
  event: any,
  activeStep: number,
  pricingTiers: any[],
  divisionSettings: any[] = [],
  eventId?: string,
  clientData: { tickets?: any[]; extras?: any[]; eventSponsors?: any[]; faqs?: any[] } = {}
): StepStatus[] {
  const { tickets = [], extras = [], eventSponsors = [], faqs = [] } = clientData;

  // Real-time existence checks for sections that store data in linked tables.
  const { data: configured = {} } = useQuery({
    queryKey: ["step-configured", eventId, activeStep],
    staleTime: 0,
    enabled: !!eventId,
    queryFn: async () => {
      const tableCount = async (table: string) => {
        const { count } = await (supabase as any)
          .from(table)
          .select("id", { count: "exact", head: true })
          .eq("event_id", eventId);
        return (count || 0) > 0;
      };
      const [
        circuit,
        constraints,
        scheduleRequests,
        tracking,
        banners,
        sponsorLinks,
        format,
        levels,
        hotels,
        faqLinks,
        coachForm,
        mediaForm,
        coachPricing,
      ] = await Promise.all([
        tableCount("circuit_event_links"),
        tableCount("event_constraint_offerings"),
        tableCount("schedule_constraints"),
        tableCount("event_team_tracking"),
        tableCount("ad_placements"),
        tableCount("event_sponsors"),
        tableCount("event_format_config"),
        tableCount("event_registration_levels"),
        tableCount("hotel_event_assignments"),
        (async () => {
          const { count } = await (supabase as any)
            .from("event_faq_links")
            .select("id", { count: "exact", head: true })
            .eq("event_id", eventId)
            .eq("is_active", true);
          return (count || 0) > 0;
        })(),
        (async () => {
          const { count } = await (supabase as any)
            .from("event_form_configs")
            .select("id", { count: "exact", head: true })
            .eq("event_id", eventId)
            .eq("form_type", "coach_attendance");
          return (count || 0) > 0;
        })(),
        (async () => {
          const { count } = await (supabase as any)
            .from("event_form_configs")
            .select("id", { count: "exact", head: true })
            .eq("event_id", eventId)
            .eq("form_type", "media_credentials");
          return (count || 0) > 0;
        })(),
        tableCount("event_coach_pricing"),
      ]);
      return { circuit, constraints, scheduleRequests, tracking, banners, sponsorLinks, format, levels, hotels, faqLinks, coachForm, mediaForm, coachPricing };
    },
  });
  // Coach pricing: count active rows when coach attendance is paid+enabled
  const { data: activeCoachPricingCount = 0 } = useQuery({
    queryKey: ["coach-pricing-count", eventId],
    enabled: !!eventId && !!event.coach_attendance_enabled && !!event.coach_attendance_paid,
    queryFn: async () => {
      const { count } = await (supabase as any)
        .from("event_coach_pricing")
        .select("id", { count: "exact", head: true })
        .eq("event_id", eventId)
        .eq("is_active", true);
      return count || 0;
    },
  });

  // Waiver settings (for required-waiver gating)
  const { data: waiverSettings } = useQuery({
    queryKey: ["waiver-settings-status", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("event_waiver_settings")
        .select("team_waiver_id, team_waiver_required, individual_waiver_id, individual_waiver_required")
        .eq("event_id", eventId)
        .maybeSingle();
      return data;
    },
  });

  return useMemo(() => {
    const checks: Record<string, () => StepStatus> = {
      // ── REQUIRED ──
      basic: () =>
        !!event.name?.trim() &&
        !!event.sport_id &&
        !!event.event_type_id &&
        !!event.start_date &&
        !!event.gender
          ? "complete"
          : "incomplete",

      venues: () => (!!event.main_venue_id ? "complete" : "incomplete"),

      registration: () => {
        const activeRows = (divisionSettings || []).filter(
          (d: any) => d.is_active && (d.has_boys || d.has_girls)
        );
        if (activeRows.length === 0) return "incomplete";
        const priced = activeRows.some(
          (d: any) =>
            (d.has_boys && Number(d.boys_price) > 0) ||
            (d.has_girls && Number(d.girls_price) > 0)
        );
        return priced ? "complete" : "incomplete";
      },

      waivers: () => {
        const teamReq = !!waiverSettings?.team_waiver_required;
        const indReq = !!waiverSettings?.individual_waiver_required;
        if (teamReq && !waiverSettings?.team_waiver_id) return "error";
        if (indReq && !waiverSettings?.individual_waiver_id) return "error";
        return "complete";
      },

      // ── OPTIONAL: "complete" only when actually configured, else "incomplete" ──
      standings: () => ((configured as any).format ? "complete" : "incomplete"),
      circuit: () =>
        (configured as any).circuit || (event as any).circuit_step_answered
          ? "complete"
          : "incomplete",

      levels: () => ((configured as any).levels ? "complete" : "incomplete"),
      scheduling: () => (event.scheduling_tiers_enabled ? "complete" : "incomplete"),
      constraints: () => ((configured as any).constraints ? "complete" : "incomplete"),
      scheduling_requests: () =>
        (configured as any).scheduleRequests ? "complete" : "incomplete",
      waitlist: () => (event.waitlist_enabled ? "complete" : "incomplete"),
      tracking: () => ((configured as any).tracking ? "complete" : "incomplete"),
      extras: () =>
        tickets.length > 0 ||
        extras.length > 0 ||
        !!event.use_external_tickets ||
        !!event.ticket_display_text?.trim()
          ? "complete"
          : "incomplete",
      details: () =>
        event.event_format?.trim() ||
        event.rules?.trim() ||
        event.checkin_instructions?.trim() ||
        event.promo_video_url?.trim() ||
        (event.gallery_images ?? []).length > 0
          ? "complete"
          : "incomplete",
      sponsors: () =>
        eventSponsors.length > 0 ||
        (event.selected_sponsor_ids ?? []).length > 0 ||
        (configured as any).sponsorLinks
          ? "complete"
          : "incomplete",
      banners: () =>
        (configured as any).banners || (event as any).banners_not_needed
          ? "complete"
          : "incomplete",
      hotels: () =>
        event.hotels_info?.trim() || (configured as any).hotels
          ? "complete"
          : "incomplete",
      faqs: () =>
        faqs.length > 0 || (configured as any).faqLinks ? "complete" : "incomplete",
      notifications: () =>
        event.coach_notification_email?.trim() ||
        (event.registration_alert_emails ?? []).length > 0 ||
        (event.payment_notification_emails ?? []).length > 0 ||
        (event.roster_upload_emails ?? []).length > 0
          ? "complete"
          : "incomplete",

      coach_attendance: () => {
        if (!event.coach_attendance_enabled) return "complete"; // intentionally off
        if (event.coach_attendance_paid && activeCoachPricingCount === 0) return "error";
        return "complete";
      },

      media_credentials: () => {
        if (!event.media_credentials_enabled) return "complete";
        // when paid, require a price > 0
        if (event.media_credentials_paid && !(Number(event.media_credentials_price) > 0)) {
          return "error";
        }
        return "complete";
      },

      coaches_media: () => {
        const coachOn = !!event.coach_attendance_enabled || !!event.enable_coach_info;
        const mediaOn = !!event.media_credentials_enabled || !!event.enable_media_credentials;
        const coachConfigured =
          coachOn ||
          (configured as any).coachForm ||
          (configured as any).coachPricing ||
          !!event.coach_notification_email?.trim() ||
          !!event.coaches_external_url?.trim() ||
          !!event.coaches_short_description?.trim();
        const mediaConfigured =
          mediaOn ||
          (configured as any).mediaForm ||
          !!event.media_credentials_notification_email?.trim() ||
          !!event.media_credentials_short_description?.trim() ||
          Number(event.media_credentials_price) > 0;
        if (!coachConfigured && !mediaConfigured) return "incomplete";
        // Only flag a real error when a PAID media credential has no price set.
        if (mediaOn && event.media_credentials_paid && !(Number(event.media_credentials_price) > 0))
          return "error";
        return "complete";
      },

      stay_to_play: () => "complete", // off=complete; no required sub-config beyond toggle
    };

    return steps.map((s, i) => {
      if (i === activeStep) return "active";
      const check = checks[s.id] ?? (() => "complete" as StepStatus);
      return check();
    });
  }, [steps, event, activeStep, pricingTiers, divisionSettings, activeCoachPricingCount, waiverSettings, configured, tickets, extras, eventSponsors, faqs]);
}

/* ─── Main Editor ────────────────────────────────────────── */
export default function RLeventsEventEditorPage() {
  const { eventId: routeEventId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const aiPrefillApplied = useRef(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { organization } = useOrganization();
  // Stamp NEW events with the admin's OWN org (org admins) or the host org
  // (super admins) so the events RLS WITH CHECK passes. Using the domain-based
  // host org for an org admin causes the insert to be rejected ("Save failed").
  const { createOrgId } = useAdminOrgScope();

  const effectiveEventId =
    routeEventId && routeEventId !== "new" ? routeEventId : undefined;
  const [activeStepId, setActiveStepId] = useState(() => readStoredEditorStep(effectiveEventId));
  const [isEditingName, setIsEditingName] = useState(false);

  // Callback: after a new event is created, redirect to its real URL
  const handleCreated = useCallback(
    (newId: string) => {
      navigate(`/admin-dashboard/events/${newId}/edit?step=${activeStepId}`, { replace: true });
    },
    [navigate, activeStepId]
  );

  const {
    event,
    updateEvent,
    markDirtyFromRelated,
    venues,
    setVenues,
    sports,
    eventTypes,
    categories,
    sponsors,
    setSponsors,
    eventSponsors,
    setEventSponsors,
    pricingTiers,
    setPricingTiers,
    extras,
    setExtras,
    tickets,
    setTickets,
    faqs,
    setFaqs,
    waiverSignatures,
    discountPolicies,
    divisionSettings,
    setDivisionSettings,
    setDivisions,
    loading,
    saving,
    dirty,
    saveError,
    save,
    lastSavedAt,
    validationErrors,
    persistedId,
  } = useEventEditor(effectiveEventId, {
    onCreated: handleCreated,
    organizationId: createOrgId || organization.id,
  });

  // Tooltip: show once then dismiss
  const [showAutoSaveTooltip, setShowAutoSaveTooltip] = useState(true);
  useEffect(() => {
    if (showAutoSaveTooltip) {
      const t = setTimeout(() => setShowAutoSaveTooltip(false), 5000);
      return () => clearTimeout(t);
    }
  }, [showAutoSaveTooltip]);

  // Relative time ticker
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!lastSavedAt) return;
    const interval = setInterval(() => setTick((n) => n + 1), 5000);
    return () => clearInterval(interval);
  }, [lastSavedAt]);

  const timeAgo = useCallback((date: Date) => {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 5) return "just now";
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ago`;
  }, []);

  const handleRetry = async () => {
    try {
      await save();
    } catch {
      toast.error(saveError || "Failed to save event");
    }
  };

  // Check if AI description was passed
  useEffect(() => {
    const aiDesc = searchParams.get("ai_description");
    if (aiDesc && !effectiveEventId) {
      updateEvent({ description: aiDesc });
    }
  }, [searchParams, effectiveEventId, updateEvent]);

  // Apply AI-builder prefill passed via navigation state
  useEffect(() => {
    if (aiPrefillApplied.current) return;
    // Read prefill from navigation state OR sessionStorage. sessionStorage is
    // the reliable source because the post-create navigation replaces the URL
    // and drops location.state, and the event row may be created before the
    // sports reference data finishes loading.
    let prefill = (location.state as any)?.aiPrefill;
    if (!prefill) {
      try {
        const stored = sessionStorage.getItem("rle_ai_prefill");
        if (stored) prefill = JSON.parse(stored);
      } catch {
        // ignore
      }
    }
    if (!prefill) return;
    // Wait for reference data (sports) so we can resolve names to ids.
    if (!sports || sports.length === 0) return;
    aiPrefillApplied.current = true;
    try {
      sessionStorage.removeItem("rle_ai_prefill");
    } catch {
      // ignore
    }

    const updates: any = {};
    if (prefill.name) updates.name = prefill.name;
    if (prefill.start_date) updates.start_date = prefill.start_date;
    if (prefill.end_date) updates.end_date = prefill.end_date;
    if (prefill.venue_id) updates.main_venue_id = prefill.venue_id;
    if (prefill.venue_name) updates.venue_name = prefill.venue_name;
    if (prefill.venue_city) updates.venue_city = prefill.venue_city;
    if (prefill.venue_state) updates.venue_state = prefill.venue_state;
    if (Array.isArray(prefill.additional_venue_ids) && prefill.additional_venue_ids.length > 0) {
      updates.additional_venue_ids = prefill.additional_venue_ids;
    }

    if (prefill.gender) {
      const g = String(prefill.gender).toLowerCase();
      // Map AI gender output to the labels the Basic Info select uses.
      updates.gender =
        g.startsWith("b") || g.startsWith("m")
          ? "Men"
          : g.startsWith("g") || g.startsWith("w")
            ? "Women"
            : "Co-ed";
    }

    if (prefill.sport) {
      const match = sports.find(
        (s: any) =>
          s.name?.toLowerCase() === String(prefill.sport).toLowerCase() ||
          s.name?.toLowerCase().includes(String(prefill.sport).toLowerCase())
      );
      if (match) updates.sport_id = match.id;
    }

    // Divisions may be an array of objects ({ name, price? }) from the AI
    // builder, or an array of plain strings (legacy). Normalize both.
    const rawDivisions: any[] = Array.isArray(prefill.divisions) ? prefill.divisions : [];
    const normDivisions = rawDivisions
      .map((d) =>
        typeof d === "string"
          ? { name: d, price: null }
          : { name: d?.name ?? "", price: d?.price ?? null }
      )
      .filter((d) => d.name);

    if (normDivisions.length > 0) {
      updates.age_groups_description = normDivisions.map((d) => d.name).join(", ");
    }

    if (Object.keys(updates).length > 0) updateEvent(updates);

    // Base price = the event's default entry price. Fall back to legacy entry_price.
    const basePrice = Number(prefill.base_price ?? prefill.entry_price) || 0;
    const cap = prefill.team_cap != null ? Number(prefill.team_cap) : null;
    const resolvedSportId = updates.sport_id as string | undefined;
    const resolvedOrgId = createOrgId || event?.organization_id;

    // The Registration step renders divisions from the org-level division_catalog
    // (linked to the sport) joined with per-event event_division_settings rows.
    // So we must (1) ensure a catalog division exists for each AI division and
    // (2) build event_division_settings with the right gender + price so they
    // actually appear and persist.
    if (normDivisions.length > 0 && resolvedSportId && resolvedOrgId) {
      const g = updates.gender;
      const hasBoys = g !== "Women";
      const hasGirls = g !== "Men";

      (async () => {
        try {
          // Existing catalog divisions for this org (any sport).
          const { data: existing } = await supabase
            .from("division_catalog")
            .select("id, label")
            .eq("organization_id", resolvedOrgId)
            .eq("is_active", true);
          const byLabel = new Map<string, string>();
          (existing ?? []).forEach((d: any) =>
            byLabel.set(String(d.label).trim().toLowerCase(), d.id)
          );

          // Existing sport links so we only add missing ones.
          const existingIds = (existing ?? []).map((d: any) => d.id);
          const linkedToSport = new Set<string>();
          if (existingIds.length > 0) {
            const { data: links } = await supabase
              .from("division_catalog_sports")
              .select("division_id, sport_id")
              .in("division_id", existingIds)
              .eq("sport_id", resolvedSportId);
            (links ?? []).forEach((l: any) => linkedToSport.add(l.division_id));
          }

          let order = 0;
          const settingsRows: any[] = [];
          for (const d of normDivisions) {
            const key = d.name.trim().toLowerCase();
            let catalogId = byLabel.get(key);
            // Create the catalog division if it doesn't exist yet.
            if (!catalogId) {
              const { data: created } = await supabase
                .from("division_catalog")
                .insert({
                  organization_id: resolvedOrgId,
                  sport_id: resolvedSportId, // legacy required column
                  label: d.name,
                  display_order: order,
                })
                .select()
                .single();
              if (created?.id) {
                catalogId = created.id;
                byLabel.set(key, catalogId);
              }
            }
            if (!catalogId) continue;
            // Ensure the division is linked to this sport.
            if (!linkedToSport.has(catalogId)) {
              await supabase
                .from("division_catalog_sports")
                .insert({ division_id: catalogId, sport_id: resolvedSportId });
              linkedToSport.add(catalogId);
            }
            const price =
              d.price != null && !Number.isNaN(Number(d.price))
                ? Number(d.price)
                : basePrice;
            settingsRows.push({
              id: "",
              event_id: event?.id || "",
              catalog_division_id: catalogId,
              has_boys: hasBoys,
              has_girls: hasGirls,
              boys_price: hasBoys ? price : 0,
              girls_price: hasGirls ? price : 0,
              boys_max_teams: cap,
              girls_max_teams: cap,
              accepts_special_requests: false,
              special_request_notes: "",
              special_request_fee: 0,
              is_active: true,
              display_order: order,
            });
            order += 1;
          }

          if (settingsRows.length > 0) {
            setDivisionSettings?.(settingsRows);
            markDirtyFromRelated?.();
          }
        } catch (err) {
          console.error("AI prefill division setup failed", err);
        }
      })();
    }

    markDirtyFromRelated?.();
  }, [location.state, effectiveEventId, sports, updateEvent, setDivisionSettings, createOrgId, event?.organization_id, event?.id, markDirtyFromRelated]);



  // Publish checklist
  const { data: checklist } = useQuery({
    queryKey: ["publish-checklist", persistedId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("event_publish_checklist", {
        p_event_id: persistedId,
      });
      if (error) throw error;
      return data;
    },
    enabled: !!persistedId,
  });

  // Hotel email readiness: the +3 day hotel reminder goes to every registrant,
  // so an event whose org has that template active must resolve a booking link
  // at the event or org level — otherwise the email ships with no way to book.
  const hotelOrgId = event?.organization_id || createOrgId || null;
  const { data: hotelEmailConfig } = useQuery({
    queryKey: ["hotel-email-config", hotelOrgId],
    enabled: !!hotelOrgId,
    queryFn: async () => {
      const [tpl, settings] = await Promise.all([
        (supabase as any)
          .from("message_templates")
          .select("id, is_active")
          .eq("organization_id", hotelOrgId)
          .eq("key", "hotel_booking_reminder")
          .maybeSingle(),
        (supabase as any)
          .from("org_settings")
          .select("default_hotel_booking_url")
          .eq("organization_id", hotelOrgId)
          .maybeSingle(),
      ]);
      return {
        enabled: tpl.data?.is_active !== false,
        orgUrl: (settings.data?.default_hotel_booking_url ?? "").trim(),
      };
    },
  });

  const hotelEmailEnabled = hotelEmailConfig?.enabled === true;
  const hotelLinkResolved =
    !!(event?.hotel_booking_url || "").trim() || !!hotelEmailConfig?.orgUrl;


  // Header pill counts (registered teams, pending scheduling requests, messages sent)
  const { data: headerCounts } = useQuery({
    queryKey: ["event-header-counts", persistedId],
    queryFn: async () => {
      const [regs, reqs, msgs] = await Promise.all([
        supabase
          .from("event_registrations_v2")
          .select("id", { count: "exact", head: true })
          .eq("event_id", persistedId),
        supabase
          .from("schedule_constraints")
          .select("id", { count: "exact", head: true })
          .eq("event_id", persistedId)
          .eq("status", "pending")
          .is("deleted_at", null),
        supabase
          .from("message_sends")
          .select("id", { count: "exact", head: true })
          .eq("event_id", persistedId),
      ]);
      return {
        teams: regs.count ?? 0,
        requests: reqs.count ?? 0,
        messages: msgs.count ?? 0,
      };
    },
    enabled: !!persistedId,
    refetchOnWindowFocus: true,
  });

  const canPublish = checklist?.all_required_met === true;
  // An event with no registerable division can never be published — coaches
  // would have nothing to register into.
  const hasActiveDivision =
    (divisionSettings || []).filter(
      (d: any) => d.is_active && (d.has_boys || d.has_girls)
    ).length > 0;
  const publishChecklist = [
    { label: "Event title", met: !!event.name.trim() },
    { label: "Sport selected", met: !!event.sport_id },
    { label: "Event type selected", met: !!event.event_type_id },
    { label: "Start date set", met: !!event.start_date },
    { label: "At least one division", met: hasActiveDivision },
    { label: "No unsaved changes", met: !dirty || !!lastSavedAt },
    ...(hotelEmailEnabled
      ? [
          {
            label: "Hotel booking link (event or org default)",
            met: hotelLinkResolved,
          },
        ]
      : []),
  ];
  const allRequiredMet =
    (canPublish || publishChecklist.every((c) => c.met)) &&
    hasActiveDivision &&
    (!hotelEmailEnabled || hotelLinkResolved);


  // One canonical sequence drives the rail, step number, Previous, and Next.
  // Optional steps may be removed, but this list is never re-sorted at runtime.
  const STEPS = useMemo(() => {
    return EVENT_STEPS.filter((s) => {
      if (s.id === "waitlist" && !event.waitlist_enabled) return false;
      return true;
    });
  }, [event.waitlist_enabled]);

  const activeStep = Math.max(
    0,
    STEPS.findIndex((s) => s.id === activeStepId)
  );

  const setActiveStep = useCallback(
    (next: number | ((current: number) => number)) => {
      const nextIndex = typeof next === "function" ? next(activeStep) : next;
      const clamped = Math.max(0, Math.min(STEPS.length - 1, nextIndex));
      const nextStep = STEPS[clamped]?.id;
      if (nextStep) {
        setActiveStepId(nextStep);
        const params = new URLSearchParams(location.search);
        params.set("step", nextStep);
        navigate(`${location.pathname}?${params.toString()}`, { replace: true });
      }
    },
    [STEPS, activeStep, location.pathname, location.search, navigate]
  );

  useEffect(() => {
    if (!STEPS.some((s) => s.id === activeStepId)) {
      setActiveStepId(STEPS[0]?.id || "basic");
    }
  }, [STEPS, activeStepId]);

  useEffect(() => {
    localStorage.setItem(`rle_event_editor_step:${effectiveEventId || "new"}`, activeStepId);
  }, [effectiveEventId, activeStepId]);

  // Step statuses
  const stepStatuses = useStepStatuses(STEPS, event, activeStep, pricingTiers, divisionSettings, persistedId, { tickets, extras, eventSponsors, faqs });

  // Grouped rail structure — map each group to its visible steps (with global index)
  const railGroups = useMemo(() => {
    const assigned = new Set<string>();
    const groups = STEP_GROUPS.map((g) => {
      const items = g.stepIds
        .map((id) => {
          const index = STEPS.findIndex((s) => s.id === id);
          if (index < 0) return null;
          assigned.add(id);
          return { step: STEPS[index], index };
        })
        .filter(Boolean) as { step: StepDef; index: number }[];
      return { label: g.label, items };
    });
    // Any step not assigned to a group (safety) goes into a trailing "Other" group
    const leftovers = STEPS.map((s, index) => ({ step: s, index })).filter(
      (x) => !assigned.has(x.step.id)
    );
    if (leftovers.length > 0) {
      groups.push({ label: "Other", items: leftovers });
    }
    return groups.filter((g) => g.items.length > 0);
  }, [STEPS]);

  // Which group contains the active step
  const activeGroupLabel = useMemo(() => {
    const g = railGroups.find((grp) => grp.items.some((it) => it.index === activeStep));
    return g?.label ?? null;
  }, [railGroups, activeStep]);

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // Auto-expand the group containing the current step
  useEffect(() => {
    if (activeGroupLabel) {
      setExpandedGroups((prev) => ({ ...prev, [activeGroupLabel]: true }));
    }
  }, [activeGroupLabel]);

  const toggleGroup = useCallback((label: string) => {
    setExpandedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  }, []);


  // Waitlist and Tracking are ongoing tools, not wizard steps — they open in a dialog.
  const [toolPanel, setToolPanel] = useState<EventOpsPanel | null>(null);

  // Honor ?step=<id> param to jump to a step (e.g. waitlist link from registration)
  useEffect(() => {
    const stepId = searchParams.get("step");
    if (!stepId) return;
    if (stepId === "waitlist" || stepId === "tracking") {
      setToolPanel(stepId);
      return;
    }
    const idx = STEPS.findIndex((s) => s.id === stepId);
    if (idx >= 0 && stepId !== activeStepId) setActiveStepId(stepId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, STEPS]);


  // Render the active step's content
  const renderStepContent = () => {
    const currentId = persistedId ?? undefined;
    const stepId = STEPS[activeStep].id;
    const help = STEP_HELP[stepId];
    // Compute dynamic, per-item completion for trackable steps so the banner
    // checks off requirements live and auto-hides when everything is satisfied.
    const dynamicRequired = (() => {
      switch (stepId) {
        case "basic":
          return [
            { label: "Event title", met: !!event.name?.trim() },
            { label: "Sport", met: !!event.sport_id },
            { label: "Start date", met: !!event.start_date },
            { label: "Gender designation", met: !!event.gender },
          ];
        case "venues":
          return [
            { label: "Select a tournament headquarters", met: !!event.main_venue_id },
          ];
        case "registration": {
          const activeRows = (divisionSettings || []).filter(
            (d: any) => d.is_active && (d.has_boys || d.has_girls)
          );
          const priced =
            activeRows.length > 0 &&
            activeRows.some(
              (d: any) =>
                (d.has_boys && Number(d.boys_price) > 0) ||
                (d.has_girls && Number(d.girls_price) > 0)
            );
          return [
            {
              label: "Activate at least one division (check Boys or Girls)",
              met: activeRows.length > 0,
            },
            { label: "Set a price for each activated division", met: priced },
          ];
        }
        default:
          return undefined;
      }
    })();
    const helpProps = help
      ? dynamicRequired
        ? { ...help, required: dynamicRequired }
        : help
      : null;
    const banner = helpProps ? <StepHelpBanner stepId={stepId} {...helpProps} /> : null;
    const wrap = (node: React.ReactNode) => (
      <>
        {banner}
        {node}
      </>
    );
    switch (stepId) {
      case "basic":
        return wrap((
          <BasicInfoSection
            event={event}
            updateEvent={updateEvent}
            sports={sports}
            eventTypes={eventTypes}
            categories={categories}
          />
        ));
      case "venues":
        return wrap((
          <VenuesSection
            event={event}
            updateEvent={updateEvent}
            venues={venues}
            setVenues={setVenues}
          />
        ));
      case "registration":
        return wrap((
          <RegistrationSection
            event={event}
            updateEvent={updateEvent}
            pricingTiers={pricingTiers}
            setPricingTiers={setPricingTiers}
            discountPolicies={discountPolicies}
            divisionSettings={divisionSettings}
            setDivisionSettings={setDivisionSettings}
            onTiersChanged={markDirtyFromRelated}
          />
        ));
      case "scheduling":
        return wrap(<SchedulingSection event={event} updateEvent={updateEvent} />);
      case "circuit":
        return wrap(
          <CircuitSection event={event} updateEvent={updateEvent} eventId={currentId} />
        );
      case "standings":
        return wrap(<StandingsSection eventId={currentId} />);
      case "levels":
        return wrap(
          <LevelsSection event={event} updateEvent={updateEvent} eventId={currentId} />
        );
      case "constraints":
        return wrap((
          <ConstraintsSection
            event={event}
            updateEvent={updateEvent}
          />
        ));
      case "scheduling_requests":
        return wrap(<SchedulingRequestsSection event={{ ...event, id: currentId }} />);
      case "waitlist":
        return wrap(<WaitlistSection event={event} eventId={currentId} />);
      case "tracking":
        return wrap((
          <TrackingSection
            event={event}
            eventId={currentId}
            divisionSettings={divisionSettings}
          />
        ));
      case "extras":
        return wrap((
          <ExtrasTicketsSection
            event={event}
            updateEvent={updateEvent}
            tickets={tickets}
            setTickets={setTickets}
            extras={extras}
            setExtras={setExtras}
          />
        ));
      case "details":
        return wrap(<DetailsSection event={event} updateEvent={updateEvent} />);
      case "sponsors":
        return wrap((
          <SponsorsSection
            sponsors={sponsors}
            setSponsors={setSponsors}
            eventSponsors={eventSponsors}
            setEventSponsors={setEventSponsors}
            eventId={currentId}
            markDirtyFromRelated={markDirtyFromRelated}
          />
        ));
      case "banners":
        return wrap(<BannersSection eventId={currentId} event={event} updateEvent={updateEvent} />);
      case "coach_attendance":
        return wrap(<CoachAttendanceSection event={event} updateEvent={updateEvent} eventId={currentId} />);
      case "media_credentials":
        return wrap(<MediaCredentialsSection event={event} updateEvent={updateEvent} eventId={currentId} />);
      case "hotels":
        return wrap(<HotelsSection event={event} updateEvent={updateEvent} />);
      case "coaches_media":
        return wrap(<CoachesMediaSection event={event} updateEvent={updateEvent} eventId={currentId} />);
      case "faqs":
        return wrap(<FaqsSection event={event} eventId={currentId} />);
      case "notifications":
        return wrap(<NotificationsSection event={event} updateEvent={updateEvent} />);
      case "waivers":
        return wrap((
          <WaiversSection
            event={event}
            updateEvent={updateEvent}
            waiverSignatures={waiverSignatures}
            eventId={currentId}
          />
        ));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const isLastStep = activeStep === STEPS.length - 1;
  const isFirstStep = activeStep === 0;

  /* ─── Step status icon ─────────────────────────────────── */
  const StepIcon = ({
    status,
    index,
  }: {
    status: StepStatus;
    index: number;
  }) => {
    switch (status) {
      case "complete":
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case "active":
        return (
          <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
            <span className="text-[10px] font-bold text-white">
              {index + 1}
            </span>
          </div>
        );
      case "error":
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      default:
        return <Circle className="w-5 h-5 text-muted-foreground/40" />;
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* ── Global Header ──────────────────────────────────── */}
      <div className="border-b border-border bg-white p-4 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/admin-dashboard/events")}
                className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              {isEditingName ? (
                <input
                  autoFocus
                  value={event.name}
                  onChange={(e) => updateEvent({ name: e.target.value })}
                  onBlur={() => setIsEditingName(false)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && setIsEditingName(false)
                  }
                  className="flex-1 text-xl font-bold text-foreground bg-transparent border-b-2 border-primary outline-none min-w-0"
                />
              ) : (
                <h1
                  onClick={() => setIsEditingName(true)}
                  className="text-xl font-bold text-foreground cursor-pointer hover:text-primary transition-colors truncate"
                >
                  {event.name || "Untitled Event"}
                </h1>
              )}
            </div>
            {/* Autosave status */}
            <div className="relative ml-8 mt-0.5">
              {saving ? (
                <p className="text-xs text-amber-600 font-medium flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Saving...
                </p>
              ) : saveError ? (
                <p className="text-xs text-red-600 font-medium flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Save failed —{" "}
                  <button
                    onClick={handleRetry}
                    className="underline hover:text-red-800"
                  >
                    retry
                  </button>
                </p>
              ) : validationErrors.length > 0 && dirty ? (
                <p className="text-xs text-amber-600 font-medium flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Cannot save — check: {validationErrors.join(", ")}
                </p>
              ) : lastSavedAt ? (
                <p className="text-xs text-green-600 font-medium flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Saved {timeAgo(lastSavedAt)}
                </p>
              ) : null}
              {showAutoSaveTooltip && lastSavedAt && (
                <div className="absolute top-full left-0 mt-1 px-2 py-1 bg-foreground text-background text-[10px] rounded shadow-lg whitespace-nowrap z-50">
                  Your changes save automatically.
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {persistedId && (
              <button
                onClick={() => navigate(`/admin-dashboard/events/${persistedId}/teams`)}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-border text-foreground hover:bg-secondary/60 transition-colors"
                title="View registered teams for this event"
              >
                <Users className="w-3.5 h-3.5 text-muted-foreground" />
                Registered Teams
                {headerCounts?.teams ? (
                  <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-cyan text-cyan-foreground text-[10px] font-bold leading-none">
                    {headerCounts.teams}
                  </span>
                ) : null}
              </button>
            )}
            {persistedId && (
              <button
                onClick={() => setToolPanel("waitlist")}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-border text-foreground hover:bg-secondary/60 transition-colors"
                title="Manage teams waiting for a spot"
              >
                <Users className="w-3.5 h-3.5 text-muted-foreground" />
                Waitlist
              </button>
            )}
            {persistedId && (
              <button
                onClick={() => setToolPanel("tracking")}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-border text-foreground hover:bg-secondary/60 transition-colors"
                title="Teams confirmed but not registered through the public flow"
              >
                <CheckCircle2 className="w-3.5 h-3.5 text-muted-foreground" />
                Tracking
              </button>
            )}
            {persistedId && (
              <button
                onClick={() => setToolPanel("pay_later")}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-border text-foreground hover:bg-secondary/60 transition-colors"
                title="Teams and registrations that still owe money"
              >
                <DollarSign className="w-3.5 h-3.5 text-muted-foreground" />
                Pay Later
              </button>
            )}

            {persistedId && (
              <button
                onClick={() => navigate(`/admin-dashboard/events/${persistedId}/credentials-coaches`)}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-border text-foreground hover:bg-secondary/60 transition-colors"
                title="Review media credential applications and registered coaches"
              >
                <IdCard className="w-3.5 h-3.5 text-muted-foreground" />
                Credentials & Coaches
              </button>
            )}
            {persistedId && (
              <button
                onClick={() => navigate(`/admin-dashboard/events/${persistedId}/scheduling-requests`)}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-border text-foreground hover:bg-secondary/60 transition-colors"
                title="Review scheduling requests submitted by teams"
              >
                <CalendarCheck className="w-3.5 h-3.5 text-muted-foreground" />
                Scheduling Requests
                {headerCounts?.requests ? (
                  <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold leading-none">
                    {headerCounts.requests}
                  </span>
                ) : null}
              </button>
            )}
            {persistedId && (
              <button
                onClick={() => navigate(`/admin-dashboard/events/${persistedId}/messages`)}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-border text-foreground hover:bg-secondary/60 transition-colors"
                title="Send messages to participants"
              >
                <Send className="w-3.5 h-3.5 text-muted-foreground" />
                Messages
                {headerCounts?.messages ? (
                  <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-cyan text-cyan-foreground text-[10px] font-bold leading-none">
                    {headerCounts.messages}
                  </span>
                ) : null}
              </button>
            )}
            <button
              onClick={async () => {
                if (!persistedId) {
                  toast.error("Save the event first to preview it.");
                  return;
                }
                const { data } = await supabase
                  .from("events")
                  .select("slug")
                  .eq("id", persistedId)
                  .maybeSingle();
                const slug = data?.slug || persistedId;
                window.open(`/events/${slug}`, "_blank", "noopener,noreferrer");
              }}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-cyan text-cyan-foreground hover:bg-cyan-hover transition-colors shadow-sm"
              title="Preview public event page in a new tab"
            >
              <Eye className="w-3.5 h-3.5" />
              Preview
            </button>
            <div
              className={cn(
                "inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full select-none",
                event.status === "published"
                  ? "bg-green-50 text-green-700 border border-green-200"
                  : "bg-muted text-muted-foreground border border-border"
              )}
            >
              <span
                className={cn(
                  "w-1.5 h-1.5 rounded-full",
                  event.status === "published" ? "bg-green-500" : "bg-muted-foreground/60"
                )}
              />
              {event.status === "published" ? "Published" : "Draft"}
            </div>
          </div>
        </div>
      </div>

      {/* ── Mobile step pill bar ───────────────────────────── */}
      <div className="md:hidden border-b border-border bg-white overflow-x-auto">
        <div className="flex gap-1.5 p-3">
          {STEPS.map((step, i) => (
            <button
              key={step.id}
              onClick={() => setActiveStep(i)}
              className={cn(
                "flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors",
                i === activeStep
                  ? "bg-primary text-white"
                  : stepStatuses[i] === "complete"
                    ? "bg-green-50 text-green-700 border border-green-200"
                    : "bg-secondary text-muted-foreground"
              )}
            >
              {stepStatuses[i] === "complete" && i !== activeStep && (
                <Check className="w-3 h-3" />
              )}
              {step.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Wizard body ────────────────────────────────────── */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left rail — desktop only */}
        <nav className="hidden md:flex flex-col w-[280px] border-r border-border bg-white flex-shrink-0 overflow-y-auto">
          <div className="p-3 space-y-1">
            {railGroups.map((group) => {
              const isOpen = !!expandedGroups[group.label];
              const total = group.items.length;
              const done = group.items.filter(
                (it) => stepStatuses[it.index] === "complete"
              ).length;
              return (
                <div key={group.label} className="mb-1">
                  <button
                    onClick={() => toggleGroup(group.label)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left hover:bg-secondary transition-colors"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <ChevronDown
                        className={cn(
                          "w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform",
                          !isOpen && "-rotate-90"
                        )}
                      />
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground truncate">
                        {group.label}
                      </span>
                    </div>
                    <span className="text-[11px] font-medium text-muted-foreground flex-shrink-0">
                      {done}/{total}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="space-y-1 mt-1">
                      {group.items.map(({ step, index: i }) => {
                        const status = stepStatuses[i];
                        return (
                          <button
                            key={step.id}
                            onClick={() => setActiveStep(i)}
                            className={cn(
                              "w-full flex items-start gap-3 px-3 py-3 rounded-lg text-left transition-colors group",
                              i === activeStep
                                ? "bg-primary/5 border-l-[3px] border-l-primary -ml-px"
                                : "hover:bg-secondary"
                            )}
                          >
                            <div className="flex-shrink-0 mt-0.5">
                              <StepIcon status={status} index={i} />
                            </div>
                            <div className="min-w-0">
                              <p
                                className={cn(
                                  "text-sm font-medium",
                                  i === activeStep
                                    ? "text-primary"
                                    : "text-foreground"
                                )}
                              >
                                {step.label}
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                                {step.summary}
                              </p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Publish checklist at bottom of rail */}
          {!allRequiredMet && (
            <div className="mt-auto p-4 border-t border-border">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                Publish checklist
              </p>
              <ul className="space-y-1.5">
                {publishChecklist.map((c) => (
                  <li
                    key={c.label}
                    className="flex items-center gap-2 text-xs"
                  >
                    {c.met ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                    ) : (
                      <Circle className="w-3.5 h-3.5 text-muted-foreground/40 flex-shrink-0" />
                    )}
                    <span
                      className={
                        c.met ? "text-muted-foreground" : "text-foreground"
                      }
                    >
                      {c.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </nav>

        {/* Step content panel */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-3xl mx-auto p-6">
              {/* Step title */}
              <div className="mb-6">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                  Step {activeStep + 1} of {STEPS.length}
                </p>
                <h2 className="text-lg font-bold text-foreground">
                  {STEPS[activeStep].label}
                </h2>
              </div>

              {/* Save error banner */}
              {saveError && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm mb-6">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {saveError}
                </div>
              )}

              {/* Active section content */}
              {renderStepContent()}
            </div>
          </div>

          {/* Step navigation footer */}
          <div className="border-t border-border bg-white p-4 flex-shrink-0">
            <div className="max-w-3xl mx-auto flex items-center justify-between">
              <button
                onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
                disabled={isFirstStep}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-foreground hover:bg-secondary transition-colors text-sm font-medium disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>

              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    try {
                      await save();
                      toast.success("Saved");
                    } catch (e: any) {
                      toast.error(e?.message || "Failed to save");
                    }
                  }}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-foreground hover:bg-secondary transition-colors text-sm font-medium disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {saving ? "Saving..." : "Save"}
                </button>

                {isLastStep ? (
                  <div className="flex items-center gap-3">
                  {!hasActiveDivision && (
                    <p className="text-xs text-destructive max-w-xs text-right">
                      Add at least one division before publishing — coaches can't
                      register without one.{" "}
                      <button
                        type="button"
                        className="underline font-medium"
                        onClick={() => {
                          const idx = STEPS.findIndex((s) => s.id === "registration");
                          if (idx >= 0) setActiveStep(idx);
                        }}
                      >
                        Go to Divisions
                      </button>
                    </p>
                  )}
                  <button
                    disabled={saving || !allRequiredMet || !persistedId}
                    title={
                      !hasActiveDivision
                        ? "Add at least one division before publishing — coaches can't register without one."
                        : allRequiredMet
                        ? "Publish event"
                        : "Complete all required fields first"
                    }
                    onClick={async () => {
                      try {
                        if (dirty) await save();
                        const { error } = await (supabase as any)
                          .from("events")
                          .update({ status: "published" })
                          .eq("id", persistedId);
                        if (error) throw error;
                        toast.success("Event published");
                        navigate("/admin-dashboard/events");
                      } catch (e: any) {
                        toast.error(e?.message || "Failed to publish event");
                      }
                    }}
                    className="flex items-center gap-2 px-5 py-2 rounded-lg bg-primary text-white hover:opacity-90 transition-opacity text-sm font-medium disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    Review & Publish
                  </button>
                  </div>
                ) : (
                  <button
                    onClick={async () => {
                      try {
                        if (dirty) await save();
                      } catch (e: any) {
                        toast.error(e?.message || "Failed to save");
                        return;
                      }
                      setActiveStep((s) => Math.min(STEPS.length - 1, s + 1));
                    }}
                    disabled={saving}
                    className="flex items-center gap-2 px-5 py-2 rounded-lg bg-primary text-white hover:opacity-90 transition-opacity text-sm font-medium disabled:opacity-50"
                  >
                    {saving ? "Saving..." : "Save & Next"}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <EventOpsDialog
        panel={toolPanel}
        eventId={persistedId ?? undefined}
        onClose={() => setToolPanel(null)}
        event={event}
        divisionSettings={divisionSettings}
      />
    </div>

  );
}